import { concatBytes } from './crypto.js';
import type { WebCryptoKey } from './crypto.js';

export const FRAME_MAGIC = new Uint8Array([0x4d, 0x4c, 0x4f, 0x31]);
export const FRAME_MAJOR = 1;
export const FRAME_HEADER_BYTES = 36;
export const FRAME_TAG_BYTES = 16;
export const MAX_FRAME_PAYLOAD_BYTES = 64 * 1024;

type CryptoBufferSource = Parameters<typeof globalThis.crypto.subtle.encrypt>[2];

function asBufferSource(value: Uint8Array): CryptoBufferSource {
  return new Uint8Array(value);
}

export const FRAME_CHANNEL = {
  CONTROL: 1,
  FILE: 2,
  PRESENCE: 3,
} as const;

export const FRAME_DIRECTION = {
  BROWSER_TO_PHONE: 1,
  PHONE_TO_BROWSER: 2,
} as const;

export type FrameChannel = (typeof FRAME_CHANNEL)[keyof typeof FRAME_CHANNEL];
export type FrameDirection = (typeof FRAME_DIRECTION)[keyof typeof FRAME_DIRECTION];

export class FrameValidationError extends Error {
  constructor(
    public readonly code:
      'FRAME_FORMAT' | 'FRAME_SEQUENCE' | 'FRAME_AUTHENTICATION' | 'FRAME_TOO_LARGE',
  ) {
    super(code);
    this.name = 'FrameValidationError';
  }
}

export interface FrameInput {
  major?: number;
  channel: FrameChannel;
  direction: FrameDirection;
  sessionId: Uint8Array;
  sequence: bigint;
  plaintext: Uint8Array;
  key: WebCryptoKey;
  noncePrefix: Uint8Array;
}

export interface DecodedFrame {
  major: number;
  channel: FrameChannel;
  direction: FrameDirection;
  sessionId: Uint8Array;
  sequence: bigint;
  plaintext: Uint8Array;
}

function assertFrameInput(input: FrameInput): void {
  if (
    input.sessionId.byteLength !== 16 ||
    input.noncePrefix.byteLength !== 4 ||
    input.sequence < 1n
  ) {
    throw new FrameValidationError('FRAME_FORMAT');
  }
  if (input.plaintext.byteLength > MAX_FRAME_PAYLOAD_BYTES) {
    throw new FrameValidationError('FRAME_TOO_LARGE');
  }
}

function nonceFor(prefix: Uint8Array, sequence: bigint): Uint8Array {
  const nonce = new Uint8Array(12);
  nonce.set(prefix);
  new DataView(nonce.buffer).setBigUint64(4, sequence, false);
  return nonce;
}

function buildHeader(input: FrameInput, ciphertextLength: number): Uint8Array {
  const header = new Uint8Array(FRAME_HEADER_BYTES);
  header.set(FRAME_MAGIC, 0);
  header[4] = input.major ?? FRAME_MAJOR;
  header[5] = input.channel;
  header[6] = input.direction;
  header[7] = 0;
  header.set(input.sessionId, 8);
  const view = new DataView(header.buffer);
  view.setBigUint64(24, input.sequence, false);
  view.setUint32(32, ciphertextLength, false);
  return header;
}

function validChannel(value: number): value is FrameChannel {
  return (
    value === FRAME_CHANNEL.CONTROL ||
    value === FRAME_CHANNEL.FILE ||
    value === FRAME_CHANNEL.PRESENCE
  );
}

function validDirection(value: number): value is FrameDirection {
  return value === FRAME_DIRECTION.BROWSER_TO_PHONE || value === FRAME_DIRECTION.PHONE_TO_BROWSER;
}

export async function encodeFrame(input: FrameInput): Promise<Uint8Array> {
  assertFrameInput(input);
  const nonce = nonceFor(input.noncePrefix, input.sequence);
  const ciphertext = new Uint8Array(
    await globalThis.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: asBufferSource(nonce),
        additionalData: asBufferSource(
          buildHeader(input, input.plaintext.byteLength + FRAME_TAG_BYTES),
        ),
        tagLength: 128,
      },
      input.key,
      asBufferSource(input.plaintext),
    ),
  );
  const header = buildHeader(input, ciphertext.byteLength);
  return concatBytes(header, ciphertext);
}

export async function decodeFrame(
  frame: Uint8Array,
  key: WebCryptoKey,
  noncePrefix: Uint8Array,
  expectedMajor = FRAME_MAJOR,
): Promise<DecodedFrame> {
  if (frame.byteLength < FRAME_HEADER_BYTES + FRAME_TAG_BYTES || noncePrefix.byteLength !== 4) {
    throw new FrameValidationError('FRAME_FORMAT');
  }
  if (!FRAME_MAGIC.every((value, index) => frame[index] === value)) {
    throw new FrameValidationError('FRAME_FORMAT');
  }
  const major = frame[4]!;
  const channel = frame[5]!;
  const direction = frame[6]!;
  const reserved = frame[7]!;
  const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  const sequence = view.getBigUint64(24, false);
  const ciphertextLength = view.getUint32(32, false);
  if (
    major !== expectedMajor ||
    reserved !== 0 ||
    !validChannel(channel) ||
    !validDirection(direction) ||
    sequence < 1n
  ) {
    throw new FrameValidationError('FRAME_FORMAT');
  }
  if (
    ciphertextLength < FRAME_TAG_BYTES ||
    ciphertextLength > MAX_FRAME_PAYLOAD_BYTES + FRAME_TAG_BYTES
  ) {
    throw new FrameValidationError('FRAME_TOO_LARGE');
  }
  if (frame.byteLength !== FRAME_HEADER_BYTES + ciphertextLength) {
    throw new FrameValidationError('FRAME_FORMAT');
  }
  const header = frame.slice(0, FRAME_HEADER_BYTES);
  const ciphertext = frame.slice(FRAME_HEADER_BYTES);
  try {
    const plaintext = new Uint8Array(
      await globalThis.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: asBufferSource(nonceFor(noncePrefix, sequence)),
          additionalData: asBufferSource(header),
          tagLength: 128,
        },
        key,
        asBufferSource(ciphertext),
      ),
    );
    if (plaintext.byteLength > MAX_FRAME_PAYLOAD_BYTES)
      throw new FrameValidationError('FRAME_TOO_LARGE');
    return { major, channel, direction, sessionId: frame.slice(8, 24), sequence, plaintext };
  } catch (error) {
    if (error instanceof FrameValidationError) throw error;
    throw new FrameValidationError('FRAME_AUTHENTICATION');
  }
}

export class ReliableSequenceValidator {
  #expected = 1n;

  accept(sequence: bigint): void {
    if (sequence !== this.#expected) throw new FrameValidationError('FRAME_SEQUENCE');
    this.#expected += 1n;
  }

  get expected(): bigint {
    return this.#expected;
  }
}

export class PresenceReplayWindow {
  #highest = 0n;
  #bitmap = 0n;
  static readonly #mask = (1n << 64n) - 1n;

  accept(sequence: bigint): 'accepted' | 'duplicate' | 'stale' {
    if (sequence < 1n) throw new FrameValidationError('FRAME_SEQUENCE');
    if (this.#highest === 0n) {
      this.#highest = sequence;
      this.#bitmap = 1n;
      return 'accepted';
    }
    if (sequence > this.#highest) {
      const jump = sequence - this.#highest;
      if (jump > 64n) throw new FrameValidationError('FRAME_SEQUENCE');
      this.#bitmap = ((this.#bitmap << jump) | 1n) & PresenceReplayWindow.#mask;
      this.#highest = sequence;
      return 'accepted';
    }
    const offset = this.#highest - sequence;
    if (offset >= 64n) return 'stale';
    const bit = 1n << offset;
    if ((this.#bitmap & bit) !== 0n) return 'duplicate';
    this.#bitmap |= bit;
    return 'accepted';
  }

  get highest(): bigint {
    return this.#highest;
  }
}
